<!-- Display list of selectable Spots/Tickers to add to current Decoder -->

<template>
  <div>
    <navbar :links="links" />
    <div
      :class="{
        ...$desktopModeStyles,
        'elevation-1': scrollTop > fixedSearchThreshold,
        tools: scrollTop <= fixedSearchThreshold,
        'tools-fixed': scrollTop > fixedSearchThreshold,
      }"
    >
      <content-type-filter-menu
        ref="menu"
        :class="{'mr-3': onlyTickerContentTypeSelected}"
        :no-radio="$route.params.ctype === 'tv'"
        :no-tv="$route.params.ctype === 'radio'"
        :no-tickers="$route.params.ctype === 'radio'"
        :on-air-only="contentsQuery.onAirOnly"
      />
      <!-- Category Filter Menu -->
      <v-slide-x-transition>
        <spot-category-filter-menu
          v-if="!onlyTickerContentTypeSelected"
          v-model="contentsQuerySelectedCategoryIds"
          :categories="categories"
          :active="contentsQuerySelectedCategoryIds.length > 0"
          :disabled="loading"
          class="ml-1"
          :class="{'mr-3': !$userprofile || !$userprofile.can_use_tagging}"
        />
      </v-slide-x-transition>
      <!-- Tags Filter Menu -->
      <v-slide-x-transition>
        <tag-filter-menu
          v-if="
            !onlyTickerContentTypeSelected &&
              $userprofile &&
              $userprofile.can_use_tagging
          "
          v-model="contentsQuerySelectedTagIds"
          :tags="$store.state.common.tags"
          :active="contentsQuerySelectedTagIds.length > 0"
          :disabled="loading"
          class="ml-1 mr-3 pt-3"
        />
      </v-slide-x-transition>
      <!-- Search -->
      <v-text-field
        ref="searchbar"
        v-model="contentsQuerySearchText"
        class="searchbar-input"
        single-line
        hide-details
        :label="$t('search') + '...'"
        clearable
        append-icon="search"
        :disabled="!decoder.id"
        @click:append="focusSearch()"
        @click:clear="clearSearch()"
        @input="autoSearch(decoder.id, decoder.ctype)"
        @keyup.native.enter="fetchNextContents()"
        @keyup.native.esc="clearSearch()"
      />
    </div>
    <div
      v-if="scrollTop > fixedSearchThreshold"
      class="tools-fixed-spacer"
    />
    <content-type-filter-active-chips
      class="mb-3"
      :show-radio-chip="!$route.params.ctype === 'radio'"
      :show-tv-chips="!$route.params.ctype === 'tv'"
    />
    <!-- 'No search results' -->
    <v-list v-if="!loading && !contents.length">
      <v-list-tile>
        <v-list-tile-sub-title style="white-space: initial">
          {{ $t('noSpotsForSearch')
          }}<span v-if="lastContentsQuery.selectedCategoryIds.length > 0">
            ({{ lastContentsQuery.selectedCategoryIds.length }}
            {{ $t('categories') }})</span>
          <br>
          <span class="msr--text current-search">
            <!-- TODO: Add info about chosen content types -->
            {{ lastContentsQuery.searchText }}
          </span>
        </v-list-tile-sub-title>
      </v-list-tile>
    </v-list>
    <!-- Selectable contents limited based on current selection warning -->
    <div
      v-if="selectableContents.length < contents.length"
      class="warning--text mb-2"
    >
      {{ $t('addContentToDecoderSelectableContentsDifferWarning') }}
    </div>
    <!-- Content list (RadioSpot, TVSpotBundle, TickerMessage) -->
    <div
      v-for="(content, index) in selectableContents"
      :key="content.id"
    >
      <!-- Unfortunately using selection as default v-list-tile action
	       does not play well with the inline mini-preview-video, since
	       we cannot catch the click event here to *only* play the
	       preview, it will also always toggle selection. This is why we
	       use the dialog preview instead here, where this works.
      -->
      <tv-spot-bundle
        v-if="isTVSpotBundle(content)"
        :bundle="content"
        :decoder="decoder"
        :show-divider="index !== selectableContents.length - 1"
        :on-click="() => toggleSelected(content)"
        :highlight-title="contentIdToSelected[content.id]"
      >
        <div slot="actions">
          <v-layout
            row
            align-center
          >
            <v-checkbox
              v-model="contentIdToSelected[content.id]"
              class="ml-2 mt-0 msr--text select-spot-checkbox"
              hide-details
              color="msr"
              @click.native.stop
            />
          </v-layout>
        </div>
      </tv-spot-bundle>
      <spot
        v-if="isSpot(content)"
        data-cy="spot-candidate-tile"
        :spot="content"
        :ctype="content.ctype"
        :on-click="() => toggleSelected(content)"
        :highlight-title="contentIdToSelected[content.id]"
        :show-divider="index !== selectableContents.length - 1"
      >
        <div slot="actions">
          <v-layout
            row
            align-center
          >
            <v-checkbox
              v-model="contentIdToSelected[content.id]"
              class="ml-2 mt-0 msr--text select-spot-checkbox"
              hide-details
              color="msr"
              @click.native.stop
            />
          </v-layout>
        </div>
      </spot>
      <ticker
        v-if="isTicker(content)"
        :ticker="content"
        :on-click="() => toggleSelected(content)"
        :highlight-title="contentIdToSelected[content.id]"
        :show-divider="index !== selectableContents.length - 1"
      >
        <div slot="actions">
          <v-layout
            row
            align-center
          >
            <v-checkbox
              v-model="contentIdToSelected[content.id]"
              class="ml-2 mt-0 msr--text select-ticker-checkbox"
              hide-details
              color="msr"
              @click.native.stop
            />
          </v-layout>
        </div>
      </ticker>
    </div>
    <!-- Add selected Spots button -->
    <v-slide-y-reverse-transition>
      <v-btn
        v-if="atLeastOneContentSelected && !showConfirmationDialog"
        dark
        round
        fixed
        bottom
        right
        data-cy="continue-btn"
        class="msr z-index-1"
        :disabled="moreThanOneClosingTimeRadioSpotSelected"
        :class="$desktopModeStyles"
        @click.stop="showConfirmationDialog = true"
      >
        <v-icon left>
          arrow_forward
        </v-icon>
        {{ $t('goForward') }}
      </v-btn>
    </v-slide-y-reverse-transition>
    <!-- Infinite Scroll Spy -->
    <mugen-scroll
      :handler="onInfiniteScrollTriggered"
      :handle-on-mount="false"
      :should-handle="!$store.state.common.progress._active"
    />
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading || contentsPage < contentsNumPages"
        id="content-loading-indicator"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Confirmation Dialog -->
    <entry-creation-dialog
      ref="confirmationDialog"
      :contents="getSelectedContents()"
      :show.sync="showConfirmationDialog"
      :title="dialogTitle"
      :busy="adding"
      :show-spot-options="!onlyTickersSelected"
      :show-weekday-options="!onlyTickersSelected"
      :show-hotrotation="
        exactlyOneContentSelected && !onlyClosingTimeRadioSpotsSelected
      "
      :show-date-controls="!onlyClosingTimeRadioSpotsSelected"
      :show-timeframe-controls="!onlyClosingTimeRadioSpotsSelected"
      @aborted="handleDialogAbortion()"
      @confirmed="handleDialogConfirmation"
    />
  </div>
</template>

<script>
import {mapState} from 'vuex';
import MugenScroll from 'vue-mugen-scroll';

import fetchContents from '@/mixins/fetchContents.js';
import identifiers from '@/mixins/identifiers.js';
import rememberPageScrollPos from '@/mixins/rememberPageScrollPos.js';
import trackScrollTop from '@/mixins/trackScrollTop.js';

import ContentTypeFilterActiveChips from '@/components/ContentTypeFilterActiveChips';
import ContentTypeFilterMenu from '@/components/ContentTypeFilterMenu';
import EntryCreationDialog from '@/components/EntryCreationDialog';
import Navbar from '@/components/Navbar';
import Spot from '@/components/Spot';
import SpotCategoryFilterMenu from '@/components/SpotCategoryFilterMenu';
import TagFilterMenu from '@/components/TagFilterMenu';
import Ticker from '@/components/Ticker';
import TvSpotBundle from '@/components/TvSpotBundle';

export default {
  components: {
    MugenScroll,

    ContentTypeFilterActiveChips,
    ContentTypeFilterMenu,
    EntryCreationDialog,
    Navbar,
    Spot,
    SpotCategoryFilterMenu,
    TagFilterMenu,
    Ticker,
    TvSpotBundle,
  },
  mixins: [fetchContents, identifiers, rememberPageScrollPos, trackScrollTop],
  data() {
    return {
      links: ['addContentToDecoder'],
      showConfirmationDialog: false,
      allSelected: false,
      adding: false,
      // A map to keep track which Spots/Tickers are currently selected.
      // Spots are only added when their checkbox is clicked, so non-
      // contained Spots are considered 'not selected', as are those
      // that are contained but false.
      contentIdToSelected: {},
    };
  },
  computed: {
    ...mapState({
      decoder: (state) => state.common.decoder,
    }),
    categories() {
      return this.$store.state.common.categories;
    },
    onlyTickerContentTypeSelected() {
      return this.ctypes.length === 1 && this.ctypes[0] === 'tickers';
    },
    atLeastOneContentSelected() {
      return this.getSelectedContents().length > 0;
    },
    exactlyOneContentSelected() {
      return this.getSelectedContents().length === 1;
    },
    onlyNormalRadioSpotsSelected() {
      const selectedContents = this.getSelectedContents();
      return (
        selectedContents.length > 0 &&
        selectedContents.filter(
          (content) =>
            this.isRadioSpot(content) && !this.isClosingTimeRadioSpot(content)
        ).length === selectedContents.length
      );
    },
    onlyClosingTimeRadioSpotsSelected() {
      const selectedContents = this.getSelectedContents();
      return (
        selectedContents.length > 0 &&
        selectedContents.filter((content) =>
          this.isClosingTimeRadioSpot(content)
        ).length === selectedContents.length
      );
    },
    atLeastOneClosingTimeRadioSpotSelected() {
      return (
        this.getSelectedContents().filter((content) =>
          this.isClosingTimeRadioSpot(content)
        ).length > 0
      );
    },
    moreThanOneClosingTimeRadioSpotSelected() {
      return (
        this.getSelectedContents().filter((content) =>
          this.isClosingTimeRadioSpot(content)
        ).length > 1
      );
    },
    onlyTickersSelected() {
      let allTicker = true;
      const contents = this.getSelectedContents();
      contents.forEach((content) => {
        if (content.kind !== 'ticker') {
          allTicker = false;
          return;
        }
      });
      return this.atLeastOneContentSelected && allTicker;
    },
    selectableContents() {
      return this.contents.filter(this.isSelectableContent);
    },
    dialogTitle() {
      let template = this.$t('batchAddContentsTitle');
      let text = template.replace(
        '{{ num }}',
        this.getSelectedContents().length
      );
      return text;
    },
  },
  watch: {
    contentIdToSelected: {
      deep: true,
      handler: function () {
        // If a Spot has been selected, make the User confirm any route changes.
        if (this.atLeastOneContentSelected) {
          this.$store.commit(
            'SET_PRE_ROUTE_QUESTION',
            this.$t('selectedSpotsRouteConfirm')
          );
        } else {
          this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
        }
      },
    },
  },
  updated() {
    // We place this here, because we want to be absolutely sure that
    // e.g. for a TVDecoder we won't see any RadioSpots due to ctypes
    // being reset etc.
    this.enforceCorrectContentTypesForCurrentDecoder();
  },
  created() {
    this.initDecoder();

    if (this.contentsQueryHasChanged) {
      console.log('Invalidating contents cache due to query change');
      this.refetchContents();
    }

    this.$store.dispatch('LIST_CATEGORIES');
  },
  methods: {
    /**
     * Limit selectable contents based on what we've already selected.
     *
     * Examples:
     *
     *   - When a RadioSpot has been selected, disallow closingtime RadioSpots,
     *     as we don't want to mix them during activation (UI would be confusing).
     *
     *   - When a closingtime RadioSpot has been selected, disallow everything
     *     else, as these should be activated in isolation. More than one at a
     *     time makes no sense, as it would use the same timing.
     */
    isSelectableContent(content) {
      // If we selected a normal RadioSpot, only show those, no closingtime.
      if (
        this.onlyNormalRadioSpotsSelected &&
        this.isClosingTimeRadioSpot(content)
      ) {
        return false;
      }
      // If we selected a closingtime RadioSpot, only show that one, nothing else.
      if (this.onlyClosingTimeRadioSpotsSelected) {
        const alreadySelected = this.contentIdToSelected[content.id];
        return alreadySelected;
      }
      return true;
    },
    toggleSelected(content) {
      const newVal = !this.contentIdToSelected[content.id];
      this.$set(this.contentIdToSelected, content.id, newVal);
    },
    getSelectedContents() {
      const selected = [];
      for (let contentId in this.contentIdToSelected) {
        if (!this.contentIdToSelected.hasOwnProperty(contentId)) {
          continue;
        }
        const isSelected = this.contentIdToSelected[contentId];
        if (!isSelected) {
          continue;
        }
        for (let content of this.contents) {
          if (content.id == contentId) {
            selected.push(content);
            break;
          }
        }
      }
      return selected;
    },
    /**
     * Fetch Decoder if not yet cached in the store (or is some other Decoder).
     *
     * Unless page has been loaded from scratch we should already have
     * fetched the Decoder on the DecoderDetail page and we can reuse
     * it here.
     *
     * Returns:
     *   Promise
     */
    fetchDecoderIfNeeded() {
      if (this.decoder.id) {
        console.log('[DEBUG] Decoder from cache');
        return Promise.resolve();
      }

      console.log('[DEBUG] Fetching Decoder...');
      this.loading = true;
      return this.$store
        .dispatch('RETRIEVE_DECODER', {
          ctype: this.$route.params.ctype,
          decoderId: this.$route.params.decoderId,
        })
        .finally(() => (this.loading = false));
    },
    initDecoder() {
      this.fetchDecoderIfNeeded().then(() => {
        this.updateNavbarLinks();
      });
    },
    /**
     * Get Decoder description and insert a link to it.
     */
    updateNavbarLinks() {
      let link = {
        label: this.decoder.description,
        path: '/player/' + this.decoder.ctype + '/' + this.decoder.id,
      };

      let lastLink = {
        label: 'addContentToDecoder',
        active: false,
      };
      this.links = [link, lastLink];
    },
    reset() {
      this.contentIdToSelected = {};
      this.$refs.confirmationDialog.reset();
    },
    handleDialogAbortion() {
      this.showConfirmationDialog = false;
      this.$refs.confirmationDialog.reset();
    },
    /**
     * Args:
     *
     *   additionalData (Object):
     *
     *     overlayData (object): Optional, is passed when a TVSpot with
     *       overlay_options has been activated and must then be handed
     *       to the API call as well.
     *
     *     timeframeId (Number): Optional timeframe ID to associated with.
     */
    handleDialogConfirmation(additionalData) {
      const contents = this.getSelectedContents();
      this.addContentsToDecoder(contents, additionalData || {});
    },
    /**
     * Handle adding of Spots and Tickers.
     *
     * The user input is pulled from the dialog widget as well as received
     * as additionalData in here.
     */
    addContentsToDecoder(contents, additionalData) {
      const vm = this;

      const decoderId = Number(this.$route.params.decoderId);

      const dialogOptions = this.$refs.confirmationDialog.options;
      const startDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.startDate
        : '';
      const stopDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.stopDate
        : '';
      const weekDays = this.$refs.confirmationDialog.weekDays;
      const minutesBeforeClosing =
        this.$refs.confirmationDialog.minutesBeforeClosing;

      const spots = contents.filter((content) => {
        return this.isSpot(content) || this.isTVSpotBundle(content);
      });
      const tickers = contents.filter((content) => this.isTicker(content));

      vm.adding = true;
      const promises = [];

      // Add Spots.
      const spotIds = spots.map((spot) => spot.id);
      const decoderIds = [decoderId];
      const opts = {
        closingtime_minutes_before_closing: minutesBeforeClosing,
        hotrotation: dialogOptions.hotrotation,
        startdate: startDate,
        stopdate: stopDate,
        weekdays: weekDays,
      };
      if (additionalData.spotIdToOverlayData) {
        opts.spotIdToOverlayData = additionalData.spotIdToOverlayData;
      }
      if (
        additionalData.timeframeIds &&
        additionalData.timeframeIds.length > 0
      ) {
        opts.timeframeIds = additionalData.timeframeIds;
      }

      const promise = this.$api.createSpotEntries(
        this.$route.params.ctype,
        decoderIds,
        spotIds,
        opts
      );
      // Make sure the Spots won't show up again when trying to add more.
      promise.then((response) => {
        for (let spot of spots) {
          if (this.isClosingTimeRadioSpot(spot)) {
            // These can be activated multiple times, so keep them in the list.
            continue;
          }
          this.$store.commit('REMOVE_CONTENT_IN_LISTS', spot);
        }
      });
      promises.push(promise);

      // Add Tickers.
      for (let ticker of tickers) {
        const opts = {
          startdate: startDate,
          stopdate: stopDate,
        };
        const promise = this.$api.addTickerToTVDecoder(
          decoderId,
          ticker.id,
          opts
        );
        promise.then(() => {
          this.$store.commit('REMOVE_CONTENT_IN_LISTS', ticker);
        });
        promises.push(promise);
      }

      // Handle success.
      Promise.all(promises)
        .then((responses) => {
          vm.reset();
          vm.adding = false;
          vm.handleDialogAbortion();

          vm.$bus.$emit('alert-success', {
            message: 'spotsAddedSuccess',
            translate: true,
          });
          // Go back to Decoder which we added this to.
          vm.$store.commit('UNSET_PRE_ROUTE_QUESTION');
          vm.goToDecoder(vm.$route.params.ctype, decoderId);
        })
        .catch((error) => {
          vm.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
          // Reset.
          vm.adding = false;
        });
    },
    goToDecoder(ctype, decoderId) {
      const path = '/player/' + ctype + '/' + decoderId;
      this.$router.push(path);
    },
    focusSearch() {
      this.$refs.searchbar.focus();
    },
    clearSearch() {
      this.contentsQuerySearchText = '';
      this.refetchContents();
    },
    onInfiniteScrollTriggered() {
      this.fetchNextContents();
    },
    /**
     * Make sure current ctypes match the Decoder we are looking at.
     *
     * Since the ctypes are globally remembered e.g. from the contents
     * page, they may not match (e.g. 'tv' for a RadioDecoder). We fix
     * that here (and btw also adjust the ctype filter widget
     * accordingly so that the User can only choose options that make
     * sense).
     */
    enforceCorrectContentTypesForCurrentDecoder() {
      const isTVDecoder = this.$route.params.ctype === 'tv';
      const isRadioDecoder = this.$route.params.ctype === 'radio';

      if (isTVDecoder) {
        // Make sure we only see tv contents. Radio must always be
        // unchecked, while at least one of the tv ctypes must be
        // checked.
        this.$store.commit('SET_CONTENTS_QUERY_RADIO', false);
        if (!this.contentsQuery.tv && !this.contentsQuery.tickers) {
          this.$store.commit('SET_CONTENTS_QUERY_TV', true);
          this.$store.commit('SET_CONTENTS_QUERY_TICKERS', true);
        }
      } else if (isRadioDecoder) {
        // Make sure we only see radio contents.
        this.$store.commit('SET_CONTENTS_QUERY_RADIO', true);
        this.$store.commit('SET_CONTENTS_QUERY_TV', false);
        this.$store.commit('SET_CONTENTS_QUERY_TICKERS', false);
      }
    },
  },
  /**
   * Make sure changing Decoders invalidates the cache as needed.
   */
  beforeRouteUpdate(to, from, next) {
    next((vm) => {
      vm.initDecoder();
    });
  },
};
</script>

<style>
.input-group.input-group--selection-controls.switch
  .input-group--selection-controls__toggle.input-group--selection-controls__toggle--active,
.switch .input-group--selection-controls__ripple--active:after {
  background-color: var(--msr-color-primary) !important;
}

/* Use custom color for v-select checkboxes and their titles. */
.input-group.input-group--selection-controls.checkbox.input-group--active
  .icon {
  color: var(--msr-color-primary) !important;
}
.v-list__tile--active {
  color: var(--msr-color-primary) !important;
}
</style>

<style scoped>
.tools {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 18px;
}

.tools-fixed {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 40px;
  left: 0;
  width: 100%;
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 8px;
  background-color: white;
  z-index: 2;
}

.tools-fixed-spacer {
  margin-bottom: 70px;
}

.searchbar-input {
  max-width: 400px;
}

.current-search {
  display: flex;
  justify-content: center;
}

.scrollable {
  overflow: scroll;
}

.fixed-size-card-title {
  font-size: 1.5em;
}
</style>



// WEBPACK FOOTER //
// src/pages/AddContentToDecoder.vue